import React from 'react';
import PropTypes from 'prop-types';
import imageCompression from 'browser-image-compression';
//Material components
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Button from '@material-ui/core/Button';
import NotchedOutline from '@material-ui/core/OutlinedInput/NotchedOutline';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
//Icones
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
//Utils
import { withStyles } from '@material-ui/core';
import clsx from 'clsx';

//Constantes de tamanho
const SIZE_MEDIUM = 'medium';
const SIZE_SMALL = 'small';

const styles = (theme) => {
    const borderColor =
      theme.palette.type === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)';
  
    return {
        /* Estilo aplicado ao elemento principal */
        root: {
            ...theme.typography.body1,
            borderRadius: theme.shape.borderRadius,
            color: theme.palette.text.primary,
            lineHeight: '1.1876em', // Reset (19px), match the native input line-height
            boxSizing: 'border-box', // Prevent padding issue with fullWidth.
            position: 'relative',
            cursor: 'text',
            display: 'inline-flex',
            alignItems: 'center',
            width: '100%',
            padding: theme.spacing(1),
            '&:hover $notchedOutline': {
                borderColor: theme.palette.text.primary,
            },
            '&$focused $notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
            '&$error $notchedOutline': {
                borderColor: theme.palette.error.main,
            },
            '&$disabled $notchedOutline': {
                borderColor: theme.palette.action.disabled,
            },
            // Reset on touch devices, it doesn't add specificity
            '@media (hover: none)': {
                '&:hover $notchedOutline': {
                    borderColor,
                },
            },
        },
        /* Pseudo classes, aplicadas de acordo com o status do componente */
        error: {},
        focused: {},
        disabled: {},
        /* Estilo aplicado ao componente quando utiliza a cor secundária */
        colorSecondary: {
            '&$focused $notchedOutline': {
                borderColor: theme.palette.secondary.main,
            },
        },
        /* Estilo aplicado à borda do componente */
        notchedOutline: {
            borderColor,
        },
        //Tamanho máximo para o componente de imagem
        image: {
            maxWidth: theme.spacing(12),
            maxHeight: theme.spacing(12),
        },
        imageSmall: {
            //Correção para um bug no chrome que não redimensiona a imagem
            maxWidth: 'calc(100% - 37px)'
        },
        //Estilo do botão de seleção de imagem
        button: {
            marginRight: theme.spacing(1)
        }
    };
};

/**
 * Componente para selecionar uma imagem no dispositivo
 */
class ImageInput extends React.Component {

    constructor(props) {
        super(props);
        //Bindings
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleFileReaderLoad = this.handleFileReaderLoad.bind(this);
        this.handleButtonClick = this.handleButtonClick.bind(this);
        //Refs
        this.inputRef = React.createRef();
        //Initial state
        this.state = {
            loading: false
        };
    }

    /**
     * Evento disparado quando um arquivo é selecionado
     * @param {*} event Objeto contendo os dados do evento
     */
    handleInputChange(event) {
        //Verifica se algum arquivo foi recebido
        if (event.target.files.length === 0) {
            return;
        }
        
        //Somente continua se houver apenas um arquivo selecionado
        if (event.target.files.length > 1) {
            this.dispararEventoErro('Somente um arquivo é permitido');
            return;
        }

        //Captura o arquivo selecionado
        const file = event.target.files[0];

        //Verifica se é uma imagem
        if (!/^image\/\S*/.test(file.type))
        {
            this.dispararEventoErro('O arquivo selecionado deve ser uma imagem');
            return;
        }

        this.readAndCompress(file);
    }

    /**
     * Realiza a leitura do arquivo selecionado, comprimindo para o tamanho desejado
     * @param {*} file Arquivo a ser comprimido
     */
    async readAndCompress(file) {
        try {
            //Indica que o componente está processando
            this.setState({ loading: true });

            //Realiza a compressão e a leitura como base64
            const opcoes = { 
                maxSizeMB: this.props.maxSize,
                exifOrientation: 1 //Para padronizar a orientação das imagens
            };
            const comprimido = await imageCompression(file, opcoes);
            const result = await imageCompression.getDataUrlFromFile(comprimido);

            //Dispara o evento de alteração
            this.dispararEventoChange(result);
            //Indica que o componente não está mais processando
            this.setState({ loading: false });
        } catch (error) {
            //Dispara o evento de erro
            this.dispararEventoErro(error);
            //Indica que o componente não está mais processando
            this.setState({ loading: false });
        }
    }

    /**
     * Evento disparado quando o conteúdo do arquivo é lido
     * @param {*} event Objeto contendo os dados do evento
     */
    handleFileReaderLoad(event) {
        if (event.target.error) {
            this.dispararEventoErro(event.target.error);
        } else {
            this.dispararEventoChange(event.target.result);
        }
    }

    /**
     * Trata o evento de click do botão para captura da imagem
     */
    handleButtonClick() {
        //Verifica se a referência está disponível e dispara o click
        if (this.inputRef.current)
            this.inputRef.current.click();
    }

    /**
     * Verifica e invoca o evento de erro recebido no construtor
     * @param {String} mensagem Mensagem de erro
     */
    dispararEventoErro(mensagem) {
        if (this.props.onError)
            this.props.onError(this, mensagem);
    }

    /**
     * Verifica e invoca o evento de alteração recebido no construtor
     * @param {String} valor Conteúdo da image
     */
    dispararEventoChange(valor) {
        if (this.props.onChange)
            this.props.onChange(this, valor);
    }

    /**
     * Renderiza o botão de escolha de acordo com o tamanho do componente
     * @param {String} size Tamanho do componente
     * @param {*} classes Classes CSS
     */
    renderizarBotao(size, classes) {
        //Para o pequeno, renderiza apenas o ícone
        if (size === SIZE_SMALL) {
            return (
                <IconButton 
                    className={classes.button} 
                    color="primary"
                    size="small" 
                    onClick={this.handleButtonClick} 
                >
                    <AddPhotoAlternateIcon />
                </IconButton>
            );
        }
        else 
            return (
                <Button className={classes.button} variant="contained" onClick={this.handleButtonClick}>
                    Selecionar
                </Button>
            );
    }

    /**
     * Verifica os parâmetros e renderiza o componente de imagem de acordo com eles
     * @param {String} value Imagem atual selecionada
     * @param {String} placeholder Url com a image já gravada
     * @param {String} size Indicação do tamanho do componente
     * @param {Boolean} loading Indica se o componente está processando
     * @param {*} classes Classes CSS do componente
     */
    renderizarImagem(value, placeholder, size, loading, classes) {
        if (loading) {
            return <CircularProgress size={25} color="secondary" />
        }

        if (value || placeholder) {
            const classNames = clsx(classes.image, { [classes.imageSmall]: size === SIZE_SMALL });
            return (<img alt="Imagem selecionada" src={value || placeholder} className={classNames} />);
        } else if (size === SIZE_MEDIUM)
            return (<span>Nenhuma imagem selecionada</span>);
    }

    /**
     * Renderiza a borda do componente, dependendo do tamanho do mesmo
     * @param {String} size Tamanho do componente
     * @param {String} label Label do componente
     * @param {*} classes Classes CSS
     */
    renderizarBorda(size, label, classes) {
        //Para tamanho pequeno, não rederiza a borda
        if (size === SIZE_SMALL)
            return null;
        else 
            return (
                <NotchedOutline
                    className={classes.notchedOutline}
                    label={label}
                    labelWidth={0}
                    notched
                />
            );
    }

    render() {
        const { label, errorMessage, value, placeholder, classes, size } = this.props;
        const { loading } = this.state;

        return (
            <FormControl error={!!errorMessage} variant="outlined" fullWidth >
                <InputLabel htmlFor="image-select-input" error={!!errorMessage} shrink >{label}</InputLabel>
                <div className={clsx(classes.root, { [classes.error]: !!errorMessage })}>
                    <input ref={this.inputRef} id="image-select-input" hidden type="file" accept="image/*" onChange={this.handleInputChange} />

                    { this.renderizarBotao(size, classes) }
                    { this.renderizarImagem(value, placeholder, size, loading, classes) }
                    { this.renderizarBorda(size, label, classes) }

                </div>
                <FormHelperText error={!!errorMessage}>{errorMessage}</FormHelperText>
            </FormControl>
        );
    }
}

ImageInput.propTypes = {
    /** Nome do componente */
    name: PropTypes.string.isRequired,
    /** Título apresentado */
    label: PropTypes.string.isRequired,
    /** Evento disparado quando uma imagem é selecionada */
    onChange: PropTypes.func.isRequired,
    /** O valor atual do componente */
    value: PropTypes.string,
    /** O valor a ser exibido caso não possua um valor atual */
    placeholder: PropTypes.string,
    /** A mensagem de erro a ser exibida */
    errorMessage: PropTypes.string,
    /** O tamanho máximo da imagem em megabytes */
    maxSize: PropTypes.number,
    /** Evento disparado quando um erro ocorre */
    onError: PropTypes.func,
    /** Define o tamanho e a aparência do componente */
    size: PropTypes.oneOf([ SIZE_MEDIUM, SIZE_SMALL ]),
    /** Classes CSS do componente */
    classes: PropTypes.any
};

ImageInput.defaultProps = {
    maxSize: 1,
    errorMessage: '',
    size: 'medium'
};

export default withStyles(styles, { name: 'ImageInput' })(ImageInput);